import {
  ViewerApp,
  AssetManagerPlugin,
  GBufferPlugin,
  timeout,
  ProgressivePlugin,
  TonemapPlugin,
  SSRPlugin,
  SSAOPlugin,
  DiamondPlugin,
  FrameFadePlugin,
  GLTFAnimationPlugin,
  GroundPlugin,
  BloomPlugin,
  TemporalAAPlugin,
  AnisotropyPlugin,
  GammaCorrectionPlugin,
  addBasePlugins,
  ITexture,
  TweakpaneUiPlugin,
  AssetManagerBasicPopupPlugin,
  CanvasSnipperPlugin,
  IViewerPlugin,
  MeshStandardMaterial2,
  Color,
  AssetImporter,

  // Color, // Import THREE.js internals
  // Texture, // Import THREE.js internals
} from 'webgi';
import './styles.css';

import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

async function setupViewer() {
  // Initialize the viewer
  const viewer = new ViewerApp({
    canvas: document.getElementById('webgi-canvas') as HTMLCanvasElement,
  });

  // Add some plugins
  const manager = await viewer.addPlugin(AssetManagerPlugin);
  const camera = viewer.scene.activeCamera;
  const position = camera.position;
  const target = camera.target;
  const exitButton = document.querySelector('.button--exit') as HTMLElement;
  const customizerRims = document.querySelector(
    '.customizer--container'
  ) as HTMLElement;
  const customizerCar = document.querySelector(
    '.customizer--container-2'
  ) as HTMLElement;

  // Add a popup(in HTML) with download progress when any asset is downloading.
  await viewer.addPlugin(AssetManagerBasicPopupPlugin);

  // Add plugins individually.
  // await viewer.addPlugin(GBufferPlugin);
  // await viewer.addPlugin(new ProgressivePlugin(32));
  // await viewer.addPlugin(new TonemapPlugin(!viewer.useRgbm));
  // await viewer.addPlugin(GammaCorrectionPlugin);
  // await viewer.addPlugin(SSRPlugin);
  // await viewer.addPlugin(SSAOPlugin);
  //   await viewer.addPlugin(DiamondPlugin);
  //   await viewer.addPlugin(FrameFadePlugin);
  //   await viewer.addPlugin(GLTFAnimationPlugin);
  //   await viewer.addPlugin(GroundPlugin);
  // await viewer.addPlugin(BloomPlugin);
  //   await viewer.addPlugin(TemporalAAPlugin);
  //   await viewer.addPlugin(AnisotropyPlugin);

  // or use this to add all main ones at once.
  await addBasePlugins(viewer);

  // Add more plugins not available in base, like CanvasSnipperPlugin which has helpers to download an image of the canvas.
  await viewer.addPlugin(CanvasSnipperPlugin);

  // LOADER
  const importer = manager.importer as AssetImporter;

  importer.addEventListener('onProgress', (ev) => {
    const progressRatio = ev.loaded / ev.total;
    // console.log(progressRatio)
    document
      .querySelector('.progress')
      ?.setAttribute('style', `transform: scaleX(${progressRatio})`);
  });

  importer.addEventListener('onLoad', (ev) => {
    gsap.to('.loader', {
      x: '100%',
      duration: 0.8,
      ease: 'power4.inOut',
      delay: 1,
      onComplete: () => {
        document.body.style.overflowY = 'auto';
      },
    });
  });

  // This must be called once after all plugins are added.
  viewer.renderer.refreshPipeline();

  await manager.addFromPath('./assets/porsche911-rwb1kB.glb');

  //DEBUG
  // const car = viewer.scene.findObjectsByName('Cylinder_4')[0];
  const rimsMaterial = manager.materials!.findMaterialsByName(
    'M_Rims'
  )[0] as MeshStandardMaterial2;
  const carBodyMaterial = manager.materials!.findMaterialsByName(
    'M_Body'
  )[0] as MeshStandardMaterial2;
  // console.log(car);
  // rimsMaterial.color = new Color(0xff0000);
  const options = viewer.scene.activeCamera.controls;
  options.minDistance = 3.35;
  options.maxDistance = 9;
  options.maxPolarAngle = 1.5;
  options.enablePan = false;
  // console.log(options);

  viewer.getPlugin(TonemapPlugin)!.config!.clipBackground = true;
  viewer.scene.activeCamera.setCameraOptions({ controlsEnabled: false });
  // viewer.getPlugin(TonemapPlugin)!.config!.tonemapBackground = true;

  function setupScrollAnimation() {
    const tl = gsap.timeline();

    // FIRST SECTION ANIMATION
    tl.to(position, {
      x: 6.75,
      y: 0.09,
      z: -0.46,
      scrollTrigger: {
        trigger: '.second',
        start: 'top bottom',
        end: 'top top',
        scrub: true,
        immediateRender: false,
      },
      onUpdate,
    })
      .to('.section--one--container', {
        opacity: 0,
        scrollTrigger: {
          trigger: '.second',
          start: 'top bottom',
          end: 'top 80%',
          scrub: 1,
          immediateRender: false,
        },
      })
      .to(target, {
        x: 1.22,
        y: 0.51,
        z: 0.68,
        scrollTrigger: {
          trigger: '.second',
          start: 'top bottom',
          end: 'top top',
          scrub: true,
          immediateRender: false,
        },
        onUpdate,
      })
      // SECOND SECTION ANIMATION
      .to(position, {
        x: -5.28,
        y: 0.55,
        z: -4.23,
        scrollTrigger: {
          trigger: '.third',
          start: 'top bottom',
          end: 'top top',
          scrub: true,
          immediateRender: false,
        },
        onUpdate,
      })
      .to(target, {
        x: 0.73,
        y: 0.43,
        z: 0.78,
        scrollTrigger: {
          trigger: '.third',
          start: 'top bottom',
          end: 'top top',
          scrub: true,
          immediateRender: false,
        },
      });
  }

  setupScrollAnimation();

  let needsUpdate = true;

  function onUpdate() {
    needsUpdate = true;
    // viewer.renderer.resetShadows();
    viewer.setDirty();
  }

  viewer.addEventListener('preFrame', () => {
    if (needsUpdate) {
      camera.positionTargetUpdated(true);
      needsUpdate = false;
    }
  });

  //DISCOVER EVENT
  document.querySelector('.button--hero')?.addEventListener('click', () => {
    const element = document.querySelector('.second');
    window.scrollTo({
      top: element?.getBoundingClientRect().top,
      left: 0,
      behavior: 'smooth',
    });
  });

  //NEXT EVENT
  document.querySelector('.next')?.addEventListener('click', () => {
    const third = document.querySelector('.button--footer');
    window.scrollTo({
      top: third?.getBoundingClientRect().top,
      left: 0,
      behavior: 'smooth',
    });
  });

  //SCROLL TO TOP EVENT
  document.querySelectorAll('.button--footer')?.forEach((item) => {
    item.addEventListener('click', () => {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      });
    });
  });

  //CUSTOMIZE EVENT
  const sections = document.querySelector('.container') as HTMLElement;
  const mainContainer = document.getElementById(
    'webgi-canvas-container'
  ) as HTMLElement;

  document
    .querySelector('.button--customize')
    ?.addEventListener('click', () => {
      viewer.scene.activeCamera.setCameraOptions({
        controlsEnabled: true,
      });
      sections.style.visibility = 'hidden';
      mainContainer.style.pointerEvents = 'all';
      document.body.style.cursor = 'grab';

      gsap.to(position, {
        x: 4.46,
        y: 0.96,
        z: 0.5,
        duration: 2,
        ease: 'power3.inOut',
        onUpdate,
      });
      gsap.to(target, {
        x: 0.56,
        y: 0.02,
        z: 0.28,
        duration: 2,
        ease: 'power3.inOut',
        onUpdate,
        onComplete: enableControlers,
      });
    });

  function enableControlers() {
    viewer.scene.activeCamera.setCameraOptions({ controlsEnabled: true });
    exitButton.style.display = 'block';
    customizerRims.style.display = 'block';
    customizerCar.style.display = 'block';
  }

  //EXIT CUSTOMIZE EVENT
  exitButton.addEventListener('click', () => {
    viewer.scene.activeCamera.setCameraOptions({ controlsEnabled: false });
    sections.style.visibility = 'visible';
    mainContainer.style.pointerEvents = 'none';
    document.body.style.cursor = 'default';
    exitButton.style.display = 'none';
    customizerRims.style.display = 'none';
    customizerCar.style.display = 'none';

    gsap.to(position, {
      x: -5.28,
      y: 0.55,
      z: -4.23,
      duration: 1,
      ease: 'power3.inOut',
      onUpdate,
    });
    gsap.to(target, {
      x: 0.73,
      y: 0.43,
      z: 0.78,
      duration: 1,
      ease: 'power3.inOut',
      onUpdate,
    });
  });

  //COLOR CHANGE EVENTS
  // !!!!!! RIMS !!!!!!!
  document
    .querySelector('.button--rims.naruto')
    ?.addEventListener('click', () => {
      changeRimsColor(new Color(0xf2a30b).convertSRGBToLinear());
    });
  document
    .querySelector('.button--rims.crimson')
    ?.addEventListener('click', () => {
      changeRimsColor(new Color(0xdc143c).convertSRGBToLinear());
    });
  document
    .querySelector('.button--rims.white')
    ?.addEventListener('click', () => {
      changeRimsColor(new Color(0xf0f0f0).convertSRGBToLinear());
    });
  document
    .querySelector('.button--rims.ultramarine')
    ?.addEventListener('click', () => {
      changeRimsColor(new Color(0x3a61f0).convertSRGBToLinear());
    });
  document
    .querySelector('.button--rims.sakura')
    ?.addEventListener('click', () => {
      changeRimsColor(new Color(0xfcc9b9).convertSRGBToLinear());
    });
  document
    .querySelector('.button--rims.green')
    ?.addEventListener('click', () => {
      changeRimsColor(new Color(0x08f26e).convertSRGBToLinear());
    });

  // !!!!!! CAR BODY !!!!!!!!
  document
    .querySelector('.button--car.naruto')
    ?.addEventListener('click', () => {
      changeBodyColor(new Color(0xf2a30b).convertSRGBToLinear());
    });
  document
    .querySelector('.button--car.crimson')
    ?.addEventListener('click', () => {
      changeBodyColor(new Color(0xdc143c).convertSRGBToLinear());
    });
  document
    .querySelector('.button--car.black')
    ?.addEventListener('click', () => {
      changeBodyColor(new Color(0x1e1e1e).convertSRGBToLinear());
    });
  document
    .querySelector('.button--car.ultramarine')
    ?.addEventListener('click', () => {
      changeBodyColor(new Color(0x3a61f0).convertSRGBToLinear());
    });
  document
    .querySelector('.button--car.sakura')
    ?.addEventListener('click', () => {
      changeBodyColor(new Color(0xfcc9b9).convertSRGBToLinear());
    });
  document
    .querySelector('.button--car.green')
    ?.addEventListener('click', () => {
      changeBodyColor(new Color(0x08f26e).convertSRGBToLinear());
    });

  function changeRimsColor(_colorToBeChanged: Color) {
    rimsMaterial.color = _colorToBeChanged;
    viewer.scene.setDirty();
  }
  function changeBodyColor(_colorToBeChanged: Color) {
    carBodyMaterial.color = _colorToBeChanged;
    viewer.scene.setDirty();
  }
}

setupViewer();
